/** Refill dialog — shows current pricing and clinic selection, then creates the order. */
import { useState } from 'react'
import { toast } from 'sonner'

import {
  type ClinicId,
  calculateOrderTotal,
  formatClinicPrice,
  formatRegionPrice,
  type TreatmentWithRelations,
} from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useRefillTreatment } from '@/frontend/hooks/api/patients/use-refill-treatment'

export function RefillDialog({
  rx,
  patientId,
  open,
  onOpenChange,
  nested = false,
}: {
  rx: TreatmentWithRelations
  patientId: string
  open: boolean
  nested?: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { region } = useViewerRegion()
  const originalOrder = rx.orders[0]
  const [clinicId, setClinicId] = useState<ClinicId | null>(originalOrder?.clinicId ?? null)

  const { mutateAsync: refill, isPending } = useRefillTreatment()

  const product = rx.product
  if (!product) return null

  const unitPrice = product.unitPrice
  const discountBps = originalOrder?.discountBps ?? 0
  const totalPrice = calculateOrderTotal({ unitPrice, quantity: rx.quantity, discountBps })

  // Price in the selected clinic's currency (what the refill will be billed in),
  // falling back to the viewer's region before a clinic is chosen.
  const fmtPrice = (amount: number) =>
    clinicId ? formatClinicPrice(amount, clinicId) : formatRegionPrice(amount, region)

  async function handleConfirm() {
    if (!clinicId) return
    try {
      await refill({ treatmentId: rx.id, clinicId, patientId })
      toast.success('Refill order created')
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create refill')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent nested={nested}>
        <DialogHeader>
          <DialogTitle>Refill Prescription</DialogTitle>
          <DialogDescription>
            Create a new order for {rx.productInfo.name} at current pricing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Product</span>
            <span className="font-medium">{rx.productInfo.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Quantity</span>
            <span>
              {rx.quantity} {product.unit}
              {rx.quantity !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Unit price</span>
            <span>{fmtPrice(unitPrice)}</span>
          </div>
          {discountBps > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Discount</span>
              <span>{(discountBps / 100).toFixed(0)}%</span>
            </div>
          )}
          <div className="flex justify-between font-medium border-t pt-2">
            <span>Total</span>
            <span>{fmtPrice(totalPrice)}</span>
          </div>

          <div className="space-y-1.5 pt-1">
            <p className="text-muted-foreground">Fulfilling clinic</p>
            <ClinicSearch value={clinicId} onChange={setClinicId} placeholder="Select clinic" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending || !clinicId}>
            {isPending ? 'Creating…' : 'Create Refill Order'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
